import { useMemo, useState } from 'react';
import { useForm } from '@mantine/form';
import { parseISO } from 'date-fns';
import { ParkingService } from '../../models/ParkingService.ts';
import { RegularRule, SpecificRule } from '../../models/ParkingRate.ts';
import {
  REGULAR_MAX_END,
  computeRegularEndDate,
  periodsOverlap,
} from '../../utils/ParkingRateConverters.ts';
import {
  computeDowSubset,
  dateToStr,
  getDatesInRange,
} from '../../utils/ParkingRateTime.ts';

type LocalRuleType = 'regular' | 'specific';

interface UseParkingRateFormParams {
  service?: ParkingService;
  initialRule?: RegularRule | SpecificRule;
  isEditMode: boolean;
  ruleTypeParam?: 'regular' | 'specific';
  ruleIndex?: number;
}

export function useParkingRateForm({
  service,
  initialRule,
  isEditMode,
  ruleTypeParam,
  ruleIndex,
}: UseParkingRateFormParams) {
  const form = useForm({
    initialValues: {
      name: initialRule?.name ?? (isEditMode ? '' : '一般費率'),
      startDate: initialRule
        ? (parseISO(
            initialRule.effectivePeriod.startDateTime.split('T')[0],
          ) as Date | null)
        : (null as Date | null),
      endDate: initialRule
        ? (parseISO(
            initialRule.effectivePeriod.endDateTime.split('T')[0],
          ) as Date | null)
        : (null as Date | null),
      description: initialRule?.description ?? '',
      entryBufferTime:
        initialRule?.additionalRules?.entryBufferTime?.toString() ?? '',
      isResetEveryDay: initialRule?.additionalRules?.isResetEveryDay ?? false,
    },
    validate: {
      name: (v) => (v.trim() ? null : ' '),
      startDate: (v) => (v ? null : ' '),
    },
  });
  const { startDate, endDate } = form.values;

  const [localRuleType, setLocalRuleType] = useState<LocalRuleType>(
    (ruleTypeParam as LocalRuleType | undefined) ?? 'regular',
  );

  // ── 一般費率自動計算結束日 ────────────────────────────────────────────
  const regularComputedEndDate = useMemo<string | null>(() => {
    if (localRuleType !== 'regular' || !startDate) return null;
    const startStr = dateToStr(startDate);
    const otherRules = (service?.parkingRate?.regularRules ?? []).filter(
      (_, idx) =>
        !(isEditMode && ruleTypeParam === 'regular' && idx === ruleIndex),
    );
    return computeRegularEndDate(startStr, otherRules);
  }, [localRuleType, startDate, service, isEditMode, ruleTypeParam, ruleIndex]);

  // ── Regular DOW subset（effectivePeriod < 7 天時只顯示有效的星期幾）───────
  const regularDowSubset = useMemo<number[]>(() => {
    if (localRuleType !== 'regular' || !startDate) return [0, 1, 2, 3, 4, 5, 6];
    const endStr = regularComputedEndDate ?? REGULAR_MAX_END;
    return computeDowSubset(dateToStr(startDate), endStr);
  }, [localRuleType, startDate, regularComputedEndDate]);

  // ── Derived: dates array for specific mode ───────────────────────────────
  const specialDates = useMemo(() => {
    if (localRuleType !== 'specific' || !startDate || !endDate) return [];
    return getDatesInRange(dateToStr(startDate), dateToStr(endDate));
  }, [localRuleType, startDate, endDate]);

  // ── 即時日期驗證：純粹是 startDate/endDate 等值的函式，不需要用 state+effect
  // 額外存一份、多一次 render——直接算成 derived value，每個 render 都即時正確。
  const overlapError = useMemo<string | null>(() => {
    if (!startDate) return null;

    if (localRuleType === 'regular') {
      // 一般費率：只檢查是否有完全相同的起始日
      if (!service?.parkingRate) return null;
      const targetStart = dateToStr(startDate);
      const duplicate = (service.parkingRate.regularRules ?? []).find(
        (rule, idx) => {
          if (isEditMode && ruleTypeParam === 'regular' && idx === ruleIndex)
            return false;
          return (
            rule.effectivePeriod.startDateTime.split('T')[0] === targetStart
          );
        },
      );
      return duplicate ? `已存在相同起始日的費率「${duplicate.name}」` : null;
    }

    // 特殊費率：檢查日期順序 + 區間重疊
    if (!endDate) return null;
    if (startDate > endDate) return '結束日不可早於起始日';
    if (!service?.parkingRate) return null;
    const targetStart = dateToStr(startDate);
    const targetEnd = dateToStr(endDate);
    const conflicting = (service.parkingRate.specificRules ?? []).find(
      (rule, idx) => {
        if (isEditMode && ruleTypeParam === 'specific' && idx === ruleIndex)
          return false;
        const ruleStart = rule.effectivePeriod.startDateTime.split('T')[0];
        const ruleEnd = rule.effectivePeriod.endDateTime.split('T')[0];
        return periodsOverlap(targetStart, targetEnd, ruleStart, ruleEnd);
      },
    );
    if (!conflicting) return null;
    const cs = conflicting.effectivePeriod.startDateTime.split('T')[0];
    const ce = conflicting.effectivePeriod.endDateTime.split('T')[0];
    return `與「${conflicting.name}」的生效期間重疊（${cs} – ${ce}）`;
  }, [
    startDate,
    endDate,
    localRuleType,
    service,
    isEditMode,
    ruleTypeParam,
    ruleIndex,
  ]);

  return {
    form,
    localRuleType,
    setLocalRuleType,
    regularComputedEndDate,
    regularDowSubset,
    specialDates,
    overlapError,
  };
}
